import React, { useState, useEffect, useRef } from 'react';
import GuideModal from './GuideModal';
import axios from 'axios';
import API_URL from '../config';
import logger from '../utils/logger';
import GoalWizard from './GoalWizard';
import StandardWidthContainer from './StandardWidthContainer';
import './DesktopComponentStyles.css';

const MVP = () => {
  const [mvp, setMVP] = useState({
    mission: '',
    vision: [], // Vision goals (year or season)
    principles: [],
    aboveLineBehaviors: [],
    belowLineBehaviors: []
    // definitionOfSuccess hidden in MVP
  });

  const [editing, setEditing] = useState({
    mission: false,
    vision: false,
    principles: false,
    aboveLineBehaviors: false,
    belowLineBehaviors: false
    // definitionOfSuccess hidden in MVP
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    fetchMVP();
    
    // Check for locally stored MVP data
    const localMVPData = localStorage.getItem('mvpData');
    if (localMVPData) {
      try {
        const parsedData = JSON.parse(localMVPData);
        // Only use local data if it has content
        if (parsedData && (parsedData.mission || 
            (parsedData.vision && parsedData.vision.length) || 
            (parsedData.principles && parsedData.principles.length))) {
          
          logger.debug('Found locally stored MVP data', parsedData);
          setMVP(prevMVP => {
            // Merge with any server data, preferring local data
            const mergedData = { ...prevMVP, ...parsedData };
            
            // Ensure arrays have at least 3 entries for UI
            ['vision', 'principles', 'aboveLineBehaviors', 'belowLineBehaviors'].forEach(field => {
              if (!Array.isArray(mergedData[field])) {
                mergedData[field] = [];
              }
              // Filter out empty strings before ensuring minimum length
              mergedData[field] = mergedData[field].filter(item => item && item.trim() !== '');
              while (mergedData[field].length < 3) {
                mergedData[field].push('');
              }
            });
            
            return mergedData;
          });
        }
      } catch (error) {
        logger.error('Error parsing local MVP data:', error);
      }
    }
  }, []);

  const fetchMVP = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const hasLocalContent = localStorage.getItem('mvpDataHasContent') === 'true';
      const localMVPData = localStorage.getItem('mvpData');
      let localData = null;
      
      // First check if we have valid localStorage data
      if (localMVPData) {
        try {
          localData = JSON.parse(localMVPData);
          logger.debug('Found MVP data in localStorage', localData);
          
          // Check if the data has actual content
          const hasContent = hasLocalContent || 
                          localData.mission || 
                          (localData.vision && localData.vision.some(item => item && item.trim() !== '')) ||
                          (localData.principles && localData.principles.some(item => item && item.trim() !== ''));
          
          if (hasContent) {
            logger.info('Using MVP data from localStorage because it has content');
            // Process the data
            const processedData = { ...localData };
            
            // Ensure arrays have at least 3 entries
            ['vision', 'principles', 'aboveLineBehaviors', 'belowLineBehaviors'].forEach(field => {
              if (!Array.isArray(processedData[field])) {
                processedData[field] = [];
              }
              // Filter out empty strings first
              processedData[field] = processedData[field].filter(item => item && item.trim() !== '');
              while (processedData[field].length < 3) {
                processedData[field].push('');
              }
            });
            
            setMVP(processedData);
            setLoading(false);
            
            // Try to fetch from server in the background, but don't wait for it
            fetchFromServerInBackground(token);
            return;
          }
        } catch (parseError) {
          logger.error('Error parsing local MVP data:', parseError);
        }
      }
      
      // If we don't have valid localStorage data, try the server
      try {
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        const response = await axios.get(`${baseUrl}/api/users/mvp`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        logger.debug('MVP data fetched successfully from server', response.data);
        
        // Convert string fields to arrays if needed
        const data = { ...response.data };
        
        // Handle vision field conversion from string to array
        if (typeof data.vision === 'string' && data.vision.trim() !== '') {
          data.vision = [data.vision];
        } else if (!Array.isArray(data.vision)) {
          data.vision = [];
        }
        
        // Filter out empty strings
        ['vision', 'principles', 'aboveLineBehaviors', 'belowLineBehaviors'].forEach(field => {
          if (!Array.isArray(data[field])) {
            data[field] = [];
          }
          data[field] = data[field].filter(item => item && item.trim() !== '');
          while (data[field].length < 3) {
            data[field].push('');
          }
        });
        
        // Save to localStorage for future use
        localStorage.setItem('mvpData', JSON.stringify(data));
        localStorage.setItem('mvpDataHasContent', 'true');
        localStorage.setItem('mvpDataLastUpdated', new Date().toISOString());
        
        setMVP(data);
        setError('');
      } catch (serverError) {
        if (serverError.response && serverError.response.status === 404) {
          // Graceful fallback for new users or missing data
          const emptyData = {
            mission: '',
            vision: ['', '', ''],
            principles: ['', '', ''],
            aboveLineBehaviors: ['', '', ''],
            belowLineBehaviors: ['', '', '']
          };
          
          setMVP(emptyData);
          setLoading(false);
          // Set a small timeout to ensure the component is fully rendered
          setTimeout(() => {
            setWizardOpen(true);
          }, 300);
          return;
        } else {
          throw serverError; // Let the outer catch block handle it
        }
      }
    } catch (error) {
      logger.error('Error fetching MVP data', error);
      
      // Last resort: check local storage again
      const localMVPData = localStorage.getItem('mvpData');
      if (localMVPData) {
        try {
          const parsedData = JSON.parse(localMVPData);
          logger.debug('Using local MVP data as last resort', parsedData);
          
          // Process the data
          const fallbackData = { ...parsedData };
          
          // Ensure arrays have at least 3 entries
          ['vision', 'principles', 'aboveLineBehaviors', 'belowLineBehaviors'].forEach(field => {
            if (!Array.isArray(fallbackData[field])) {
              fallbackData[field] = [];
            }
            fallbackData[field] = fallbackData[field].filter(item => item && item.trim() !== '');
            while (fallbackData[field].length < 3) {
              fallbackData[field].push('');
            }
          });
          
          setMVP(fallbackData);
          setError('Unable to connect to server. Using locally saved data.');
        } catch (e) {
          setError('Failed to load MVP. Please try again.');
        }
      } else {
        // If all else fails, initialize with empty data
        setMVP({
          mission: '',
          vision: ['', '', ''],
          principles: ['', '', ''],
          aboveLineBehaviors: ['', '', ''],
          belowLineBehaviors: ['', '', '']
        });
        setError('Failed to load MVP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to fetch from server in background
  const fetchFromServerInBackground = async (token) => {
    try {
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const response = await axios.get(`${baseUrl}/api/users/mvp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      logger.debug('Background fetch from server successful', response.data);
      // We don't update the state here to avoid disrupting the user experience
    } catch (error) {
      logger.warn('Background fetch from server failed, continuing with localStorage data', error.message);
    }
  };

  const handleChange = (field, value) => {
    logger.debug(`Updating ${field} field`);
    setMVP(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setMVP(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleAdd = (field) => {
    setMVP(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemove = (field, index) => {
    setMVP(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (field) => {
    try {
      setMessage('');
      setError('');
      
      // Get the current value for the field
      const value = mvp[field];
      
      // Save to local storage first (as a backup)
      saveToLocalStorage(field, value);
      
      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save changes');
        return;
      }
      
      // Map the field name to the expected backend field name
      const fieldMapping = {
        'mission': 'mission',
        'vision': 'vision',
        'processGoals': 'principles'
      };
      
      const backendField = fieldMapping[field] || field;
      
      // Prepare data for API request - use the correct field name for the backend
      let data = {};
      
      // If we're updating a field that needs special handling
      if (field === 'mission') {
        data = { mission: value };
      } else if (field === 'vision') {
        data = { vision: Array.isArray(value) ? value.filter(item => item && item.trim() !== '') : [value] };
      } else if (field === 'processGoals') {
        data = { principles: Array.isArray(value) ? value.filter(item => item && item.trim() !== '') : [value] };
      } else {
        // For other fields, use the mapping
        data = { [backendField]: value };
      }
      
      // Log what we're trying to save
      logger.debug(`Saving ${field} to backend:`, data);
      logger.debug(`Making API request to ${API_URL}/api/users/mvp with data:`, JSON.stringify(data));
      
      // Add a test request to verify the endpoint is working
      try {
        const testResponse = await axios.get(
          `${API_URL}/api/users/mvp`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        logger.debug('Test GET request to /api/users/mvp succeeded:', testResponse.data);
      } catch (testError) {
        logger.error('Test GET request to /api/users/mvp failed:', testError);
        logger.error('Error details:', testError.response?.data || 'No response data');
      }
      
      // Make API request to save the data
      const response = await axios.put(
        `${API_URL}/api/users/mvp`, 
        data,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Handle successful response
      if (response.status === 200) {
        logger.debug(`Successfully saved ${field} to backend:`, response.data);
        logger.debug(`Successfully saved ${field} to backend:`, response.data);
        setMessage(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully!`);
        
        // Exit editing mode
        setEditing(prev => ({
          ...prev,
          [field]: false
        }));
        
        // Update local storage to indicate we have content
        localStorage.setItem('mvpDataHasContent', 'true');
        
        // Also save to local storage as backup
        saveToLocalStorage(field, value);
      } else {
        // Handle unexpected status codes
        logger.error(`Unexpected status code when saving ${field}:`, response.status);
        setError(`Error saving ${field}. Please try again.`);
      }
    } catch (error) {
      // Handle API errors
      logger.error(`Error saving ${field} to backend:`, error);
      
      // Get the current value for the field (needed in catch block scope)
      const currentValue = mvp[field];
      
      // Set appropriate error message
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        logger.error('Error response data:', error.response.data);
        logger.error('Error response status:', error.response.status);
        setError(`Server error: ${error.response.data.message || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        logger.error('Error request:', error.request);
        setError('No response from server. Please check your internet connection and try again.');
        
        // Since server is unreachable, save to local storage as fallback
        saveToLocalStorage(field, currentValue);
      } else {
        // Something happened in setting up the request that triggered an Error
        logger.error('Error message:', error.message);
        setError(`Error: ${error.message}`);
      }
    }
  };
  
  // Function to save data to local storage
  const saveToLocalStorage = (field, value) => {
    try {
      // Get existing data
      const existingData = localStorage.getItem('mvpData');
      let mvpData = {};
      
      if (existingData) {
        try {
          mvpData = JSON.parse(existingData);
          logger.debug('Retrieved existing MVP data from localStorage', mvpData);
        } catch (parseError) {
          logger.error('Error parsing existing MVP data, starting fresh', parseError);
          mvpData = {};
        }
      }
      
      // Update the specific field
      mvpData[field] = value;
      
      // Ensure the data structure matches what the backend expects
      const completeData = { 
        ...mvp, 
        [field]: value,
        // Make sure these fields are properly initialized if they don't exist
        mission: mvpData.mission || mvp.mission || '',
        vision: Array.isArray(mvpData.vision) ? mvpData.vision.filter(item => item && item.trim() !== '') : 
                (Array.isArray(mvp.vision) ? mvp.vision.filter(item => item && item.trim() !== '') : []),
        principles: Array.isArray(mvpData.principles) ? mvpData.principles.filter(item => item && item.trim() !== '') : 
                    (Array.isArray(mvp.principles) ? mvp.principles.filter(item => item && item.trim() !== '') : [])
      };
      
      // Save back to localStorage
      localStorage.setItem('mvpData', JSON.stringify(completeData));
      localStorage.setItem('mvpDataLastUpdated', new Date().toISOString());
      
      // Set a flag indicating we have content
      localStorage.setItem('mvpDataHasContent', 'true');
      
      logger.debug(`Saved ${field} to local storage`, value);
      logger.debug('Complete MVP data in localStorage', completeData);
    } catch (error) {
      logger.error('Error saving to local storage:', error);
    }
  };
  
  // Handle data from the Goal Wizard
  const handleWizardComplete = (wizardData) => {
    logger.debug('MVP: Goal Wizard completed with data:', wizardData);
    
    // Close the wizard
    setWizardOpen(false);
    
    // Extract the data from the wizard
    const { mission, vision, principles } = wizardData;
    
    // Update the MVP state with the new data
    setMVP(prevMVP => {
      // Create a new object with the updated values
      const updatedMVP = {
        ...prevMVP,
        mission: mission || prevMVP.mission,
        vision: vision || prevMVP.vision,
        principles: principles || prevMVP.principles
      };
      
      // Ensure arrays have at least 3 entries for UI
      ['vision', 'principles', 'aboveLineBehaviors', 'belowLineBehaviors'].forEach(field => {
        if (!Array.isArray(updatedMVP[field])) {
          updatedMVP[field] = [];
        }
        
        // Filter out empty strings before ensuring minimum length
        updatedMVP[field] = updatedMVP[field].filter(item => item && item.trim() !== '');
        
        while (updatedMVP[field].length < 3) {
          updatedMVP[field].push('');
        }
      });
      
      return updatedMVP;
    });
    
    // Mark motivation as completed
    markMotivationCompleted();
    
    // Save the data to the backend
    saveWizardDataToBackend(wizardData);
  };
  
  // Function to save wizard data to the backend and local storage
  const saveWizardDataToBackend = async (wizardData) => {
    try {
      setLoading(true);
      setError('');
      
      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save changes');
        setLoading(false);
        return;
      }
      
      // Map the wizard data to the format expected by the backend
      const mappedData = {
        mission: wizardData.mission || '',
        vision: wizardData.vision || [],
        principles: wizardData.processGoals || []
      };
      
      // Log what we're trying to save
      logger.debug('Saving wizard data to backend:', mappedData);
      
      // Make API request to save the data
      const response = await axios.put(
        `${API_URL}/api/users/mvp`, 
        mappedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Handle successful response
      if (response.status === 200) {
        logger.debug('Successfully saved wizard data to backend:', response.data);
        setMessage('Goal wizard data saved successfully!');
        
        // Update local storage to indicate we have content
        localStorage.setItem('mvpDataHasContent', 'true');
        
        // Also save to local storage as a backup
        localStorage.setItem('mvpData', JSON.stringify(mappedData));
      } else {
        // Handle unexpected status codes
        logger.error('Unexpected status code when saving wizard data:', response.status);
        setError('Error saving wizard data. Please try again.');
      }
    } catch (error) {
      // Handle API errors
      logger.error('Error saving wizard data to backend:', error);
      
      // Set appropriate error message
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        logger.error('Error response data:', error.response.data);
        logger.error('Error response status:', error.response.status);
        setError(`Server error: ${error.response.data.message || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        logger.error('Error request:', error.request);
        setError('No response from server. Please check your internet connection and try again.');
        
        // Since server is unreachable, save to local storage as fallback
        const mappedData = {
          mission: wizardData.mission || '',
          vision: wizardData.vision || [],
          principles: wizardData.processGoals || []
        };
        localStorage.setItem('mvpData', JSON.stringify(mappedData));
        localStorage.setItem('mvpDataHasContent', 'true');
      } else {
        // Something happened in setting up the request that triggered an Error
        logger.error('Error message:', error.message);
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const markMotivationCompleted = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save changes');
        return;
      }
      
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const response = await axios.post(`${baseUrl}/api/users/starter-task`, 
        { task: 'motivationCompleted', completed: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      logger.debug('Motivation completion status updated on server:', response.data);
      localStorage.setItem('motivationCompleted', 'true');
    } catch (error) {
      logger.error('Error updating motivation completion status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-sm text-base-content/60">Loading your goals...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <GuideModal guideKey="MVP" />
      <div className="container px-4 pt-4" style={{ width: '1024px !important', maxWidth: '1024px !important', margin: '0 auto !important', boxSizing: 'border-box !important' }}>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-4">
          Your Mission, Vision, and Process Goals
        </h1>
        
        {/* Wizard Button */}
        <button
          className="btn btn-primary btn-wide gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          onClick={() => setWizardOpen(true)}
          aria-label="Open Goal Setting Wizard"
        >
          Goal Setting Wizard
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="text-center mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <p className="text-red-500 font-medium">{error}</p>
        </div>
      )}
      
      {message && (
        <div className="alert alert-success shadow-sm mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <span className="text-sm">{message}</span>
        </div>
      )}

      {/* Global Save Button */}
      {(editing.mission || editing.vision || editing.principles) && (
        <div className="sticky top-4 z-10 mb-6">
          <div className="flex justify-center">
            <button
              onClick={() => {
                if (editing.mission) handleSubmit('mission');
                if (editing.vision) handleSubmit('vision');
                if (editing.principles) handleSubmit('principles');
                setEditing({
                  mission: false,
                  vision: false,
                  principles: false,
                  aboveLineBehaviors: false,
                  belowLineBehaviors: false
                });
              }}
              className="btn btn-primary btn-wide hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
            >
              Save All Changes
            </button>
          </div>
        </div>
      )}
      
      {/* Content Sections */}
      <div className="space-y-6">
        {/* Mission Section */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Mission</h3>
              <button
                onClick={() => setEditing({ ...editing, mission: !editing.mission })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.mission ? 'Cancel editing' : 'Edit mission'}
              >
                {editing.mission ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            <div className="min-h-[3rem]">
              {editing.mission ? (
                <textarea
                  value={mvp.mission || ''}
                  onChange={(e) => handleChange('mission', e.target.value)}
                  className="textarea textarea-bordered w-full min-h-[6rem] resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  placeholder="Enter your mission statement..."
                  rows={4}
                />
              ) : (
                <div className="prose max-w-none">
                  {mvp.mission ? (
                    <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                      {mvp.mission}
                    </p>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-base-content/50 text-sm italic">
                        Click edit to add your mission statement
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vision Section */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Vision</h3>
              <button
                onClick={() => setEditing({ ...editing, vision: !editing.vision })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.vision ? 'Cancel editing' : 'Edit vision'}
              >
                {editing.vision ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            <div className="min-h-[3rem]">
              {editing.vision ? (
                <div className="space-y-3">
                  {Array.isArray(mvp.vision) && mvp.vision.map((item, index) => (
                    <div key={index} className="card bg-base-50 border border-base-200 hover:border-base-300 transition-colors duration-200">
                      <div className="card-body p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-base-content/80">
                              {index === 0 ? 'Main Vision' : `Vision ${index + 1}`}
                            </span>
                            {index === 0 && (
                              <span className="badge badge-primary badge-xs">Required</span>
                            )}
                          </div>
                          {index > 0 && (
                            <button
                              onClick={() => handleRemove('vision', index)}
                              className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 transition-colors duration-200"
                              aria-label="Remove vision"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <textarea
                          value={item}
                          onChange={(e) => handleArrayChange('vision', index, e.target.value)}
                          className="textarea textarea-bordered w-full min-h-[4.5rem] text-sm resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          placeholder={index === 0 ? "Enter your main vision..." : `Enter vision ${index + 1}...`}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Button */}
                  {mvp.vision && mvp.vision.length < 5 && (
                    <button
                      onClick={() => handleAdd('vision')}
                      className="btn btn-outline btn-sm w-full hover:scale-[1.02] transition-all duration-200"
                    >
                      Add Vision Statement
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(mvp.vision) && mvp.vision.filter(item => item && item.trim() !== '').length > 0 ? (
                    mvp.vision.filter(item => item && item.trim() !== '').map((item, index) => (
                      <div key={index} className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-base-content/50 text-sm italic">
                        Click edit to add vision statements
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Process Goals Section */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Process Goals</h3>
              <button
                onClick={() => setEditing({ ...editing, principles: !editing.principles })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.principles ? 'Cancel editing' : 'Edit process goals'}
              >
                {editing.principles ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            <div className="min-h-[3rem]">
              {editing.principles ? (
                <div className="space-y-3">
                  {Array.isArray(mvp.principles) && mvp.principles.map((item, index) => (
                    <div key={index} className="card bg-base-50 border border-base-200 hover:border-base-300 transition-colors duration-200">
                      <div className="card-body p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-base-content/80">
                              {index === 0 ? 'Main Process Goal' : `Process Goal ${index + 1}`}
                            </span>
                            {index === 0 && (
                              <span className="badge badge-primary badge-xs">Required</span>
                            )}
                          </div>
                          {index > 0 && (
                            <button
                              onClick={() => handleRemove('principles', index)}
                              className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 transition-colors duration-200"
                              aria-label="Remove principle"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <textarea
                          value={item}
                          onChange={(e) => handleArrayChange('principles', index, e.target.value)}
                          className="textarea textarea-bordered w-full min-h-[4.5rem] text-sm resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          placeholder={index === 0 ? "Enter your main process goal..." : `Enter process goal ${index + 1}...`}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Button */}
                  {mvp.principles && mvp.principles.length < 5 && (
                    <button
                      onClick={() => handleAdd('principles')}
                      className="btn btn-outline btn-sm w-full hover:scale-[1.02] transition-all duration-200"
                    >
                      Add Process Goal
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(mvp.principles) && mvp.principles.filter(item => item && item.trim() !== '').length > 0 ? (
                    mvp.principles.filter(item => item && item.trim() !== '').map((item, index) => (
                      <div key={index} className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-base-content/50 text-sm italic">
                        Click edit to add process goals
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Above the Line Behaviors Section */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Above the Line Behaviors</h3>
              <button
                onClick={() => setEditing({ ...editing, aboveLineBehaviors: !editing.aboveLineBehaviors })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.aboveLineBehaviors ? 'Cancel editing' : 'Edit above the line behaviors'}
              >
                {editing.aboveLineBehaviors ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            <div className="min-h-[3rem]">
              {editing.aboveLineBehaviors ? (
                <div className="space-y-3">
                  {Array.isArray(mvp.aboveLineBehaviors) && mvp.aboveLineBehaviors.map((item, index) => (
                    <div key={index} className="card bg-base-50 border border-base-200 hover:border-base-300 transition-colors duration-200">
                      <div className="card-body p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-base-content/80">
                              {index === 0 ? 'Main Above the Line Behavior' : `Above the Line Behavior ${index + 1}`}
                            </span>
                            {index === 0 && (
                              <span className="badge badge-primary badge-xs">Required</span>
                            )}
                          </div>
                          {index > 0 && (
                            <button
                              onClick={() => handleRemove('aboveLineBehaviors', index)}
                              className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 transition-colors duration-200"
                              aria-label="Remove above the line behavior"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <textarea
                          value={item}
                          onChange={(e) => handleArrayChange('aboveLineBehaviors', index, e.target.value)}
                          className="textarea textarea-bordered w-full min-h-[4.5rem] text-sm resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          placeholder={index === 0 ? "Enter your main above the line behavior..." : `Enter above the line behavior ${index + 1}...`}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Button */}
                  {mvp.aboveLineBehaviors && mvp.aboveLineBehaviors.length < 5 && (
                    <button
                      onClick={() => handleAdd('aboveLineBehaviors')}
                      className="btn btn-outline btn-sm w-full hover:scale-[1.02] transition-all duration-200"
                    >
                      Add Above the Line Behavior
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(mvp.aboveLineBehaviors) && mvp.aboveLineBehaviors.filter(item => item && item.trim() !== '').length > 0 ? (
                    mvp.aboveLineBehaviors.filter(item => item && item.trim() !== '').map((item, index) => (
                      <div key={index} className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-base-content/50 text-sm italic">
                        Click edit to add above the line behaviors
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Below the Line Behaviors Section */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Below the Line Behaviors</h3>
              <button
                onClick={() => setEditing({ ...editing, belowLineBehaviors: !editing.belowLineBehaviors })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.belowLineBehaviors ? 'Cancel editing' : 'Edit below the line behaviors'}
              >
                {editing.belowLineBehaviors ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            <div className="min-h-[3rem]">
              {editing.belowLineBehaviors ? (
                <div className="space-y-3">
                  {Array.isArray(mvp.belowLineBehaviors) && mvp.belowLineBehaviors.map((item, index) => (
                    <div key={index} className="card bg-base-50 border border-base-200 hover:border-base-300 transition-colors duration-200">
                      <div className="card-body p-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-base-content/80">
                              {index === 0 ? 'Main Below the Line Behavior' : `Below the Line Behavior ${index + 1}`}
                            </span>
                            {index === 0 && (
                              <span className="badge badge-primary badge-xs">Required</span>
                            )}
                          </div>
                          {index > 0 && (
                            <button
                              onClick={() => handleRemove('belowLineBehaviors', index)}
                              className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 transition-colors duration-200"
                              aria-label="Remove below the line behavior"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <textarea
                          value={item}
                          onChange={(e) => handleArrayChange('belowLineBehaviors', index, e.target.value)}
                          className="textarea textarea-bordered w-full min-h-[4.5rem] text-sm resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          placeholder={index === 0 ? "Enter your main below the line behavior..." : `Enter below the line behavior ${index + 1}...`}
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Button */}
                  {mvp.belowLineBehaviors && mvp.belowLineBehaviors.length < 5 && (
                    <button
                      onClick={() => handleAdd('belowLineBehaviors')}
                      className="btn btn-outline btn-sm w-full hover:scale-[1.02] transition-all duration-200"
                    >
                      Add Below the Line Behavior
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(mvp.belowLineBehaviors) && mvp.belowLineBehaviors.filter(item => item && item.trim() !== '').length > 0 ? (
                    mvp.belowLineBehaviors.filter(item => item && item.trim() !== '').map((item, index) => (
                      <div key={index} className="bg-base-50 p-4 rounded-lg border border-base-200">
                        <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                          {item}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-base-content/50 text-sm italic">
                        Click edit to add below the line behaviors
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Goal Wizard */}
      <GoalWizard
        isOpen={wizardOpen}
        initialData={mvp}
        onComplete={handleWizardComplete}
        onClose={() => setWizardOpen(false)}
      />
      </div>
    </>
  );
};

export default MVP;